import React from 'react';
import { Card, CardContent, CardHeader, Box, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledCard = styled(Card)(({ theme, variant = 'default', elevation = 'medium' }) => {
  const elevations = {
    none: 'none',
    low: theme.shadows[2],
    medium: theme.shadows[4],
    high: theme.shadows[8],
  };

  const variants = {
    default: {
      background: theme.palette.mode === 'dark' 
        ? 'rgba(255, 255, 255, 0.05)' 
        : 'rgba(255, 255, 255, 0.8)',
      border: `1px solid ${theme.palette.mode === 'dark' 
        ? 'rgba(255, 255, 255, 0.1)' 
        : 'rgba(255, 255, 255, 0.3)'}`,
    },
    gradient: {
      background: theme.palette.mode === 'dark'
        ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(240, 147, 251, 0.1) 100%)'
        : 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(240, 147, 251, 0.1) 100%)',
      border: `1px solid ${theme.palette.mode === 'dark'
        ? 'rgba(102, 126, 234, 0.2)'
        : 'rgba(102, 126, 234, 0.2)'}`,
    },
    glass: {
      background: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(255, 255, 255, 0.2)',
      border: `1px solid ${theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.2)'
        : 'rgba(255, 255, 255, 0.3)'}`,
      backdropFilter: 'blur(20px)',
    },
  };

  return {
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: elevations[elevation],
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'visible',
    position: 'relative',
    ...variants[variant],
    
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[6],
    },
  };
});

const StyledCardHeader = styled(CardHeader)(({ theme }) => ({
  '& .MuiCardHeader-title': {
    fontWeight: 600,
    fontSize: '1.25rem',
    color: theme.palette.text.primary,
  },
  '& .MuiCardHeader-subheader': {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(3),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '&:last-child': {
    paddingBottom: theme.spacing(3),
  },
}));

const ModernCard = ({ 
  children, 
  title, 
  subtitle, 
  action, 
  variant = 'default', 
  elevation = 'medium',
  sx = {},
  ...props 
}) => {
  const theme = useTheme();

  return (
    <StyledCard 
      variant={variant} 
      elevation={elevation}
      sx={sx}
      {...props}
    >
      {title && (
        <StyledCardHeader
          title={title}
          subheader={subtitle}
          action={action}
        />
      )}
      
      <StyledCardContent>
        {children}
      </StyledCardContent>
    </StyledCard>
  );
};

// Specialized card variants
export const GradientCard = ({ children, ...props }) => (
  <ModernCard variant="gradient" {...props}>
    {children}
  </ModernCard>
);

export const GlassCard = ({ children, ...props }) => (
  <ModernCard variant="glass" {...props}>
    {children}
  </ModernCard>
);

export const StatsCard = ({ 
  title, 
  value, 
  subtitle, 
  icon, 
  trend, 
  trendValue, 
  color = 'primary',
  ...props 
}) => {
  const theme = useTheme();
  
  const colorGradients = {
    primary: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    secondary: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    success: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    warning: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    error: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    info: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
  };

  return (
    <ModernCard variant="glass" {...props}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" component="div" sx={{ fontWeight: 700, mb: 1 }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
          {trend && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: trend === 'up' ? 'success.main' : 'error.main',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {trend === 'up' ? '↗' : '↘'} {trendValue}
              </Typography>
            </Box>
          )}
        </Box>
        
        {icon && (
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              background: colorGradients[color],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '1.5rem',
            }}
          >
            {icon}
          </Box>
        )}
      </Box>
    </ModernCard>
  );
};

export const FeatureCard = ({ 
  title, 
  description, 
  icon, 
  action, 
  color = 'primary',
  ...props 
}) => {
  const theme = useTheme();
  
  const colorGradients = {
    primary: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    secondary: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    success: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    warning: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    error: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    info: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
  };

  return (
    <ModernCard variant="glass" {...props}>
      <Box sx={{ textAlign: 'center' }}>
        {icon && (
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: colorGradients[color],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '2rem',
              margin: '0 auto 1.5rem',
            }}
          >
            {icon}
          </Box>
        )}
        
        <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>
        
        {action && (
          <Box sx={{ mt: 2 }}>
            {action}
          </Box>
        )}
      </Box>
    </ModernCard>
  );
};

export default ModernCard; 